// The icon a merchant can put next to a category in the Woodmart navigation.
//
// Deliberately a CURATED list of names rather than "any icon": the picker has
// to be scannable, and every name here maps to an icon that already ships in
// the storefront bundle (lucide-react), so choosing one costs nothing extra to
// download.
//
// This module is pure data — no React, no icon components — so the editor
// schema, the storefront and any future surface can share one vocabulary. The
// name → component mapping lives next to the renderer that needs it.
//
// ⚠ Never rename an entry. The chosen name is persisted per category in
// store_theme.woodmart_settings; renaming one silently blanks a merchant's
// choice. Add new names, retire old ones by leaving them in place.

export interface CategoryIconDef {
  /** Stable id, persisted. Also the lucide component name in kebab form. */
  name: string;
  /** Grouping for the picker, so 40 icons stay browsable. */
  group: 'light' | 'home' | 'tools' | 'tech' | 'care' | 'general';
}

export const CATEGORY_ICONS: CategoryIconDef[] = [
  // Lighting — the category set this was built for
  { name: 'lamp', group: 'light' },
  { name: 'lamp-ceiling', group: 'light' },
  { name: 'lamp-desk', group: 'light' },
  { name: 'lamp-floor', group: 'light' },
  { name: 'lamp-wall-up', group: 'light' },
  { name: 'lightbulb', group: 'light' },
  { name: 'sun', group: 'light' },
  { name: 'sun-medium', group: 'light' },
  { name: 'flashlight', group: 'light' },
  { name: 'sparkles', group: 'light' },

  // Home & decoration
  { name: 'sofa', group: 'home' },
  { name: 'armchair', group: 'home' },
  { name: 'bed', group: 'home' },
  { name: 'lamp-wall-down', group: 'home' },
  { name: 'frame', group: 'home' },
  { name: 'image', group: 'home' },
  { name: 'clock', group: 'home' },
  { name: 'flower-2', group: 'home' },
  { name: 'cooking-pot', group: 'home' },
  { name: 'utensils', group: 'home' },
  { name: 'coffee', group: 'home' },
  { name: 'bath', group: 'home' },
  { name: 'shower-head', group: 'home' },
  { name: 'door-open', group: 'home' },

  // Tools & electrical
  { name: 'wrench', group: 'tools' },
  { name: 'hammer', group: 'tools' },
  { name: 'drill', group: 'tools' },
  { name: 'plug', group: 'tools' },
  { name: 'plug-zap', group: 'tools' },
  { name: 'zap', group: 'tools' },
  { name: 'battery-charging', group: 'tools' },
  { name: 'cable', group: 'tools' },
  { name: 'toggle-left', group: 'tools' },

  // Tech & security
  { name: 'router', group: 'tech' },
  { name: 'wifi', group: 'tech' },
  { name: 'cctv', group: 'tech' },
  { name: 'shield-check', group: 'tech' },
  { name: 'bell-ring', group: 'tech' },
  { name: 'monitor-smartphone', group: 'tech' },
  { name: 'smartphone', group: 'tech' },
  { name: 'speaker', group: 'tech' },

  // Health & care
  { name: 'heart-pulse', group: 'care' },
  { name: 'stethoscope', group: 'care' },
  { name: 'leaf', group: 'care' },
  { name: 'baby', group: 'care' },

  // General fallbacks
  { name: 'package', group: 'general' },
  { name: 'shopping-bag', group: 'general' },
  { name: 'tag', group: 'general' },
  { name: 'star', group: 'general' },
  { name: 'grid-2x2', group: 'general' },
  { name: 'layers', group: 'general' },
];

export const CATEGORY_ICON_NAMES: string[] = CATEGORY_ICONS.map((i) => i.name);

const KNOWN = new Set(CATEGORY_ICON_NAMES);

/** Is this a name we still ship? Guards against a bag holding a retired id. */
export function isKnownCategoryIcon(name: string | null | undefined): boolean {
  return typeof name === 'string' && KNOWN.has(name);
}

/** The icon chosen for a category, or null when none is set or the stored name
 *  is no longer part of the set. Keyed by SLUG: it survives a category being
 *  re-created, which an id does not, and it is what the merchant sees. */
export function categoryIconFor(
  icons: Record<string, string> | null | undefined,
  slug: string,
): string | null {
  const chosen = icons?.[slug];
  return isKnownCategoryIcon(chosen) ? (chosen as string) : null;
}

export const CATEGORY_ICON_GROUP_LABELS: Record<
  CategoryIconDef['group'],
  { fr: string; ar: string; en: string }
> = {
  light: { fr: 'Éclairage', ar: 'إضاءة', en: 'Lighting' },
  home: { fr: 'Maison & déco', ar: 'المنزل والديكور', en: 'Home & decor' },
  tools: { fr: 'Outillage & électricité', ar: 'أدوات وكهرباء', en: 'Tools & electrical' },
  tech: { fr: 'High-tech & sécurité', ar: 'تقنية وأمن', en: 'Tech & security' },
  care: { fr: 'Santé & bien-être', ar: 'الصحة والعافية', en: 'Health & care' },
  general: { fr: 'Général', ar: 'عام', en: 'General' },
};
